/**
 * Wire contract for following a local run live (change:
 * `local-run-live-view`). The CLI registers a running local run with
 * `POST /api/projects/:id/live-runs` and streams ordered, idempotent
 * events to `POST /api/projects/:id/live-runs/:runId/events`.
 *
 * Live data is display-only: the final `walkthroughs/push` bundle stays
 * the authoritative write. Field names are snake_case like the bundle.
 */
import { z } from "zod";
import {
  confidenceSchema,
  taskImpactSchema,
} from "../validation/finding-metrics.js";

export const liveRunPhaseSchema = z.enum([
  "walking",
  "completing",
  "matching",
  "judging",
  "publishing",
]);
export type LiveRunPhase = z.infer<typeof liveRunPhaseSchema>;

export const liveRunRegisterSchema = z.object({
  run_id: z.string().uuid(),
  scenario_id: z.string().uuid(),
  target_url: z.string().max(2048).nullable().optional(),
  started_at: z.string().datetime().optional(),
  lenses: z.array(z.string().min(1).max(64)).max(20).optional(),
});
export type LiveRunRegister = z.infer<typeof liveRunRegisterSchema>;

export interface LiveRunRegisterResponse {
  walkthrough_id: string;
  viewer_url: string;
  status: "created" | "resumed";
}

const seq = z.number().int().positive();

export const liveRunStepEventSchema = z.object({
  seq,
  type: z.literal("step"),
  step: z.object({
    number: z.number().int().positive(),
    title: z.string().min(1).max(500),
    persona_reasoning: z.string().max(10000).nullable().optional(),
    grounding: z.string().max(10000).nullable().optional(),
    page_url: z.string().max(4096).nullable().optional(),
    surface: z.string().max(500).nullable().optional(),
    /** PNG or JPEG, base64. Send at most one per request. */
    screenshot_base64: z.string().max(7_000_000).optional(),
  }),
});

export const liveRunActionEventSchema = z.object({
  seq,
  type: z.literal("action"),
  step_number: z.number().int().positive(),
  action_type: z.string().min(1).max(64),
  target: z.string().max(500).nullable().optional(),
});

export const liveRunFindingEventSchema = z.object({
  seq,
  type: z.literal("finding"),
  finding: z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(200),
    type: z.enum(["helps", "hinders", "missing", "irrelevant", "incorrect"]),
    severity: z.enum(["critical", "major", "minor", "nit"]),
    ui_fact: z.string().max(2000).nullable().optional(),
    consequence: z.string().max(2000).nullable().optional(),
    step_number: z.number().int().positive().nullable().optional(),
    task_impact: taskImpactSchema.nullable().optional(),
    confidence: confidenceSchema.nullable().optional(),
  }),
});

export const liveRunPhaseEventSchema = z.object({
  seq,
  type: z.literal("phase"),
  phase: liveRunPhaseSchema,
});

export const liveRunAbandonEventSchema = z.object({
  seq,
  type: z.literal("abandon"),
  reason: z.string().max(500).optional(),
});

export const liveRunEventSchema = z.discriminatedUnion("type", [
  liveRunStepEventSchema,
  liveRunActionEventSchema,
  liveRunFindingEventSchema,
  liveRunPhaseEventSchema,
  liveRunAbandonEventSchema,
]);
export type LiveRunEvent = z.infer<typeof liveRunEventSchema>;

export const LIVE_RUN_MAX_EVENTS = 50;

export const liveRunEventsSchema = z.object({
  events: z.array(liveRunEventSchema).min(1).max(LIVE_RUN_MAX_EVENTS),
});

export interface LiveRunEventsResponse {
  /** Highest seq in the batch; the CLI trims its spool up to it. */
  accepted_seq: number;
  /** The user pressed Stop in the viewer: end the walk. */
  cancelled: boolean;
}
